import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const PrivacyPolicyPage = () => {
  const { user } = useAuth();
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [userProfile, setUserProfile] = useState(null);

  // Fetch user's current SMS consent status
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          ?.from('user_profiles')
          ?.select('sms_consent, phone')
          ?.eq('id', user?.id)
          ?.single();

        if (error) throw error;

        setUserProfile(data);
        setSmsConsent(data?.sms_consent || false);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Handle SMS consent toggle
  const handleSmsConsentChange = async (checked) => {
    if (!user?.id) {
      setMessage({ type: 'error', text: 'Please sign in to update SMS consent preferences' });
      return;
    }

    if (!userProfile?.phone) {
      setMessage({ type: 'error', text: 'Please add a phone number to your profile before enabling SMS consent' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          sms_consent: checked,
          sms_consent_updated_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', user?.id);

      if (error) throw error;

      setSmsConsent(checked);
      setMessage({ 
        type: 'success', 
        text: checked 
          ? 'SMS consent enabled. You will now receive SMS messages from GROW GUARDIAN LLC.' :'SMS consent disabled. You will no longer receive SMS messages from GROW GUARDIAN LLC.'
      });

      // Clear message after 5 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating SMS consent:', error);
      setMessage({ type: 'error', text: 'Failed to update SMS consent. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Grow Guardian</title>
        <meta name="description" content="Privacy Policy for Grow Guardian - Learn how we collect, use, and protect your personal information" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 lg:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-600 mb-8">
              Effective Date: December 26, 2025
            </p>

            <div className="prose prose-lg max-w-none space-y-8">
              {/* Who We Are */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  1. Who We Are
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Grow Guardian LLC ("we," "us," or "our") operates the Grow Guardian application 
                  and website at growguardian.work. We are committed to protecting your privacy and 
                  ensuring the security of your personal information.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Contact Email:</strong> <a href="mailto:admin@growguardian.work" className="text-green-600 hover:text-green-700">admin@growguardian.work</a>
                </p>
              </section>

              {/* What Data We Collect */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  2. What Data We Collect
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We collect the following types of information to provide and improve our services:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Personal Information:</strong> Name, email address, and phone number when you create an account</li>
                  <li><strong>Account Information:</strong> Username, password (encrypted), profile preferences, and subscription details</li>
                  <li><strong>Usage Data:</strong> Analytics data about how you interact with our application, including pages visited, features used, and time spent</li>
                  <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers, and mobile network information</li>
                  <li><strong>Agricultural Data:</strong> Cultivation task information, IPM records, treatment plans, field data, and compliance documentation you choose to store</li>
                  <li><strong>Communication Data:</strong> Messages sent through our AI chat assistant and support inquiries</li>
                </ul>
              </section>

              {/* How We Use Data */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  3. How We Use Your Data
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We use your information for the following purposes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Account Access:</strong> To authenticate your identity and provide secure access to your account</li>
                  <li><strong>App Functionality:</strong> To deliver core features including task management, IPM planning, compliance tracking, and team collaboration</li>
                  <li><strong>Notifications:</strong> To send operational updates, task reminders, and account notifications via email or SMS (with your consent)</li>
                  <li><strong>Customer Support:</strong> To respond to your inquiries, troubleshoot issues, and provide technical assistance</li>
                  <li><strong>Security:</strong> To detect and prevent fraud, unauthorized access, and other illegal activities</li>
                  <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our application features and user experience</li>
                </ul>
              </section>

              {/* SMS Terms */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  4. SMS Communications and Verification
                </h2>
                
                {/* SMS Consent Checkbox - Interactive */}
                {user && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400 rounded-lg p-6 mb-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 pt-1">
                        <input
                          type="checkbox"
                          id="sms-consent-checkbox"
                          checked={smsConsent}
                          onChange={(e) => handleSmsConsentChange(e?.target?.checked)}
                          disabled={loading || !userProfile?.phone}
                          className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor="sms-consent-checkbox" className="cursor-pointer">
                          <p className="text-gray-900 font-semibold text-lg mb-2">
                            ☐ I agree to receive SMS messages from GROW GUARDIAN LLC
                          </p>
                          <p className="text-gray-700 leading-relaxed text-sm">
                            By checking this box, I agree to receive SMS messages from GROW GUARDIAN LLC at the phone number provided in my profile. 
                            Message frequency varies. Message & data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help. 
                            View our Privacy Policy and <a href="/terms-conditions" className="text-green-600 hover:text-green-700 underline">Terms & Conditions</a>.
                          </p>
                        </label>
                        
                        {!userProfile?.phone && (
                          <p className="text-amber-600 text-sm mt-2 font-medium">
                            ⚠️ Please add a phone number to your profile in <a href="/user-settings-preferences" className="underline hover:text-amber-700">Settings</a> to enable SMS consent.
                          </p>
                        )}
                        
                        {message?.text && (
                          <div className={`mt-3 p-3 rounded-lg ${
                            message?.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'
                          }`}>
                            <p className="text-sm font-medium">{message?.text}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* SMS Consent Overview */}
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-6">
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Important SMS Notice:</strong> By providing your phone number and checking the SMS consent checkbox during signup, 
                    you agree to receive SMS messages from GROW GUARDIAN LLC at the phone number provided. Message frequency varies. 
                    Message & data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for help.
                  </p>
                </div>

                {/* SMS Verification Process */}
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    4.1 SMS Verification Process
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    To ensure the security of your account and verify your phone number, we use SMS verification:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Verification Code Delivery:</strong> When you sign up or update your phone number, we send a 6-digit verification code via SMS to confirm ownership of the phone number</li>
                    <li><strong>Code Validity:</strong> Verification codes expire after 15 minutes for security purposes</li>
                    <li><strong>Resend Option:</strong> You can request a new verification code if the original expires or is not received (60-second cooldown between resend requests)</li>
                    <li><strong>Account Security:</strong> SMS verification helps prevent unauthorized access and ensures only you can access your account</li>
                  </ul>
                </div>

                {/* Consent Requirements */}
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    4.2 SMS Consent Requirements
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Your explicit consent is required before we send SMS messages to your phone number:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Opt-In Checkbox:</strong> During account creation, you must check the SMS consent checkbox to receive text messages</li>
                    <li><strong>Voluntary Consent:</strong> Providing your phone number and SMS consent is optional but required for SMS-based features like phone verification and urgent alerts</li>
                    <li><strong>Consent Scope:</strong> Your consent covers account verification codes, security alerts, urgent notifications, and operational messages related to your account</li>
                    <li><strong>No Marketing Messages:</strong> We do not send promotional or marketing SMS messages without separate explicit consent</li>
                  </ul>
                </div>

                {/* Message Types and Frequency */}
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    4.3 Message Types and Frequency
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We send the following types of SMS messages:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Verification Codes:</strong> 6-digit codes sent during signup, login, or phone number changes (sent immediately upon request)</li>
                    <li><strong>Security Alerts:</strong> Notifications about suspicious login attempts, password changes, or account security events (sent as needed)</li>
                    <li><strong>Urgent Notifications:</strong> Critical alerts related to compliance deadlines, escalated tasks, or time-sensitive operational matters (frequency varies based on your activity)</li>
                    <li><strong>Two-Factor Authentication:</strong> Authentication codes when 2FA is enabled on your account (sent during each login attempt)</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    <strong>Message Frequency:</strong> Message frequency varies based on your account activity and settings. 
                    You may receive multiple messages per day during active use, or only occasional messages for security purposes.
                  </p>
                </div>

                {/* Opt-Out Instructions */}
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    4.4 How to Opt Out (STOP) or Get Help (HELP)
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    You can control your SMS preferences at any time:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>To Opt Out:</strong> Reply <strong>STOP</strong> to any SMS message from Grow Guardian. You will receive a confirmation message and will no longer receive SMS communications from us</li>
                    <li><strong>To Get Help:</strong> Reply <strong>HELP</strong> to any SMS message to receive assistance and contact information</li>
                    <li><strong>Account Settings:</strong> You can also manage SMS preferences in your account settings within the application</li>
                    <li><strong>Email Support:</strong> Contact us at admin@growguardian.work for SMS-related questions or to update your preferences</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    <strong>Important:</strong> Opting out of SMS messages may affect certain features that require phone verification, 
                    such as two-factor authentication and urgent security alerts.
                  </p>
                </div>

                {/* Data Usage and Carrier Rates */}
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    4.5 Data Usage and Carrier Rates
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>Message and Data Rates:</strong> Standard message and data rates may apply based on your mobile carrier's plan. 
                    Grow Guardian is not responsible for any charges incurred from your mobile carrier for receiving SMS messages.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>Supported Carriers:</strong> Our SMS service works with all major U.S. mobile carriers. If you experience 
                    issues receiving messages, please contact your carrier or reach out to us at admin@growguardian.work.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Phone Number Storage:</strong> Your phone number is securely stored in our database and used only for 
                    the purposes described in this policy. We do not share your phone number with third parties except our SMS 
                    service provider (Twilio) who is contractually obligated to protect your information.
                  </p>
                </div>

                {/* SMS Service Provider */}
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 leading-relaxed">
                    <strong>SMS Service Provider:</strong> We use Twilio, a trusted third-party SMS service provider, to deliver 
                    text messages. Twilio's privacy practices are governed by their own privacy policy. For more information, 
                    visit <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 underline">Twilio's Privacy Policy</a>.
                  </p>
                </div>

                {/* Twilio Compliance Statement */}
                <div className="mt-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    4.6 Phone Number Privacy and Marketing Policy
                  </h3>
                  <div className="bg-green-50 border-l-4 border-green-500 p-5 rounded mb-4">
                    <p className="text-gray-900 font-semibold text-lg mb-2">
                      ✓ We Do NOT Sell or Share Your Phone Number for Marketing
                    </p>
                    <p className="text-gray-800 leading-relaxed">
                      <strong>Explicit Guarantee:</strong> Grow Guardian LLC does not sell, rent, lease, trade, or share your 
                      phone number with third parties for marketing, advertising, or promotional purposes. Your phone number is 
                      used exclusively for the operational and security purposes described in this Privacy Policy.
                    </p>
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>What Data We Collect (Phone-Related):</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                    <li>Phone number provided during account registration</li>
                    <li>SMS consent status (whether you opted in to receive SMS messages)</li>
                    <li>SMS delivery status and timestamps for verification codes</li>
                    <li>Opt-out requests (STOP commands) and preferences</li>
                  </ul>

                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>How We Use Your Phone Number:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                    <li><strong>Account Verification:</strong> To send 6-digit verification codes confirming phone number ownership</li>
                    <li><strong>Security Alerts:</strong> To notify you of suspicious login attempts, password changes, or security events</li>
                    <li><strong>Two-Factor Authentication:</strong> To deliver authentication codes when 2FA is enabled</li>
                    <li><strong>Urgent Operational Notifications:</strong> To send time-sensitive alerts related to compliance deadlines or critical tasks (only with your consent)</li>
                    <li><strong>Service Communications:</strong> To send transactional messages directly related to your use of Grow Guardian services</li>
                  </ul>

                  <p className="text-gray-700 leading-relaxed mb-3">
                    <strong>We Do NOT Use Your Phone Number For:</strong>
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                    <li>Marketing or promotional campaigns</li>
                    <li>Selling or sharing with advertisers, data brokers, or marketing companies</li>
                    <li>Third-party marketing purposes of any kind</li>
                    <li>Unsolicited commercial messages</li>
                  </ul>

                  <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <p className="text-gray-800 leading-relaxed">
                      <strong>Questions or Concerns?</strong> If you have any questions about how we use your phone number, 
                      SMS communications, or this Privacy Policy, please contact us at:
                    </p>
                    <p className="text-gray-900 font-semibold mt-2">
                      Email: <a href="mailto:admin@growguardian.work" className="text-green-600 hover:text-green-700 underline">admin@growguardian.work</a>
                    </p>
                    <p className="text-gray-700 mt-2">
                      We are committed to transparency and will respond to your inquiries promptly.
                    </p>
                  </div>
                </div>
              </section>

              {/* In-App Purchases and Auto-Renewing Subscriptions */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  5. In-App Purchases and Auto-Renewing Subscriptions
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Grow Guardian offers auto-renewing subscription plans available for purchase within the application. 
                  The following subscription options are available:
                </p>

                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse border border-gray-300 rounded-lg text-sm">
                    <thead>
                      <tr className="bg-green-50">
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Subscription Title</th>
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Length</th>
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Price</th>
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">Price Per Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white">
                        <td className="border border-gray-300 px-4 py-3 text-gray-800 font-medium">Grow Guardian Pro – Monthly</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">1 Month</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">$14.99 / month</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">$14.99 per month</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="border border-gray-300 px-4 py-3 text-gray-800 font-medium">Grow Guardian Pro – Annual</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">12 Months</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">$79.99 / year</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-700">~$6.67 per month</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-4">
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Auto-Renewal Notice:</strong> Subscriptions automatically renew unless auto-renew is turned off at least 
                    24 hours before the end of the current subscription period. Your account will be charged for renewal within 
                    24 hours prior to the end of the current period. You can manage and cancel your subscription in your 
                    App Store account settings after purchase. No cancellation of the current subscription is allowed during the active subscription period.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>End User License Agreement (EULA):</strong> Use of Grow Guardian is also subject to Apple's Standard 
                  Licensed Application End User License Agreement. You can review the Apple EULA at:
                </p>
                <p className="mb-4">
                  <a 
                    href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-green-600 hover:text-green-700 underline break-all"
                  >
                    https://www.apple.com/legal/internet-services/itunes/dev/stdeula/
                  </a>
                </p>
              </section>

              {/* Data Sharing */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  6. Data Sharing and Disclosure
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We do not sell your personal information. We may share your data only in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Service Providers:</strong> We work with trusted third-party providers (such as Twilio for SMS, Supabase for database hosting, and OpenAI for AI features) who help us operate our services. These providers are contractually obligated to protect your data.</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or government regulation</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity</li>
                  <li><strong>With Your Consent:</strong> We may share data with third parties when you explicitly authorize us to do so</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3 font-semibold">
                  We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </section>

              {/* Data Retention and Security */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  7. Data Retention and Security
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Data Retention:</strong> We retain your personal information for as long as your account is active or as needed to provide services. If you delete your account, we will delete or anonymize your data within 90 days, except where we are required to retain it for legal or compliance purposes.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Security Measures:</strong> We implement industry-standard security measures including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-2">
                  <li>Encrypted data transmission (SSL/TLS)</li>
                  <li>Encrypted password storage using bcrypt</li>
                  <li>Row-level security policies in our database</li>
                  <li>Regular security audits and monitoring</li>
                  <li>Access controls and authentication protocols</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
                </p>
              </section>

              {/* User Rights */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  8. Your Rights
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Depending on your jurisdiction, you may have the following rights:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information in your account settings</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails or SMS notifications at any time</li>
                  <li><strong>Data Portability:</strong> Request a machine-readable copy of your data (where applicable)</li>
                  <li><strong>Object:</strong> Object to certain processing of your personal information</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  To exercise any of these rights, please contact us at admin@growguardian.work.
                </p>
              </section>

              {/* Children's Privacy */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  9. Children's Privacy
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Grow Guardian is not directed to children under the age of 13. We do not knowingly 
                  collect personal information from children under 13. If we become aware that a child 
                  under 13 has provided us with personal information, we will take steps to delete such 
                  information. If you believe we have collected information from a child under 13, 
                  please contact us immediately at admin@growguardian.work.
                </p>
              </section>

              {/* Cookies and Tracking */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  10. Cookies and Tracking Technologies
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We use cookies and similar tracking technologies to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Maintain your login session and preferences</li>
                  <li>Analyze application usage and performance</li>
                  <li>Remember your settings and preferences</li>
                  <li>Improve security and prevent fraud</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  You can control cookies through your browser settings. Disabling cookies may affect 
                  the functionality of certain features.
                </p>
              </section>

              {/* Policy Updates */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  11. Changes to This Privacy Policy
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices 
                  or legal requirements. When we make material changes, we will notify you by email or 
                  through a prominent notice in the application. The "Effective Date" at the top of this 
                  policy indicates when it was last updated. We encourage you to review this policy 
                  periodically.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  12. Contact Us
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or 
                  our data practices, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-gray-800">
                    <strong>Grow Guardian LLC</strong><br />
                    Email: <a href="mailto:admin@growguardian.work" className="text-green-600 hover:text-green-700">admin@growguardian.work</a><br />
                    Website: <a href="https://growguardian.work" className="text-green-600 hover:text-green-700">https://growguardian.work</a>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;